import * as THREE from 'three';
import { clamp, smoothstep } from '../core/Rand.js';
import { MovementConfig } from './MovementConfig.js';

/**
 * CameraRig — everything cosmetic about the first-person view.
 *
 * Runs in `update(dt)` at the render rate and interpolates the 120 Hz
 * simulation with `ctx.time.alpha`, so the camera is perfectly smooth at any
 * framerate. Nothing here feeds back into the simulation.
 *
 * Layers, in the order they are composed:
 *   eye position  = lerp(prevFeet, feet, alpha)
 *                 + up   * (eyeHeight + bobY + landDip + mantleDip)
 *                 + right* (bobX + leanOffset)
 *   orientation   = yaw   + sway.yaw   + recoil.yaw
 *                   pitch + sway.pitch + recoil.pitch + breath + landNod
 *                   roll  = strafe + slide + lean + bob
 */

const _e = new THREE.Euler(0, 0, 0, 'YXZ');
const _v = new THREE.Vector3();
const _fwd = new THREE.Vector3();
const _right = new THREE.Vector3();
const _leanDir = new THREE.Vector3();
const _leanOrigin = new THREE.Vector3();

/** Stable critically-damped spring (Game Programming Gems 4, implicit form). */
class Spring {
  constructor(x = 0) { this.x = x; this.v = 0; }
  step(target, omega, dt, zeta = 1) {
    const f = 1 + 2 * dt * zeta * omega;
    const oo = omega * omega;
    const hoo = dt * oo;
    const hhoo = dt * hoo;
    const detInv = 1 / (f + hhoo);
    const detX = f * this.x + dt * this.v + hhoo * target;
    const detV = this.v + hoo * (target - this.x);
    this.x = detX * detInv;
    this.v = detV * detInv;
    return this.x;
  }
  set(x, v = 0) { this.x = x; this.v = v; return this; }
}

export class CameraRig {
  constructor(cfg = MovementConfig, movement) {
    this.cfg = cfg;
    this.mv = movement;
    this.ctx = null;

    /* look */
    this.yaw = 0;
    this.pitch = 0;

    /* recoil: transient part springs home, absorbed part is folded into aim */
    this.recoilPitch = new Spring(0);
    this.recoilYaw = new Spring(0);
    this.recoilHold = 0;
    this.recoilOffset = new THREE.Vector2(0, 0); // x = pitch, y = yaw (radians)

    /* sway / lag */
    this.swayPitch = new Spring(0);
    this.swayYaw = new Spring(0);
    this.viewSway = new THREE.Vector2(0, 0);     // x = yaw lag, y = pitch lag

    /* roll + lean */
    this.roll = new Spring(0);
    this.lean = new Spring(0);
    this.leanOffset = 0;
    this.leanInput = 0;      // -1 .. 1, written by PlayerSystem each frame

    /* bob */
    this.bobScale = new Spring(0);
    this.bobOffset = new THREE.Vector3(0, 0, 0);
    this.bobRoll = 0;

    /* eye height + landing */
    this.eyeHeight = cfg.eyeStand;
    this.eyeVel = 0;
    this.landDip = new Spring(0);
    this.landNod = new Spring(0);

    /* fov */
    this.fovSprint = new Spring(0);
    this.fovSpeed = new Spring(0);
    this.fov = cfg.fovBase;
    this.adsBlend = 0;
    this.adsHold = 0;

    /* breathing */
    this.breathT = 0;

    /* published */
    this.velocityLocal = new THREE.Vector3();
    this.eye = new THREE.Vector3();
    this.quaternion = new THREE.Quaternion();

    this._lastAds = 0;
  }

  init(ctx) {
    this.ctx = ctx;
    this.eyeHeight = this.cfg.eyeStand;
    this.fov = this.cfg.fovBase;
    this.eye.copy(this.mv.feet).setY(this.mv.feet.y + this.eyeHeight);
  }

  reset(yaw = 0, pitch = 0) {
    this.yaw = yaw;
    this.pitch = pitch;
    this.recoilPitch.set(0); this.recoilYaw.set(0); this.recoilHold = 0;
    this.recoilOffset.set(0, 0);
    this.swayPitch.set(0); this.swayYaw.set(0); this.viewSway.set(0, 0);
    this.roll.set(0); this.lean.set(0); this.leanOffset = 0;
    this.bobScale.set(0); this.bobOffset.set(0, 0, 0); this.bobRoll = 0;
    this.leanInput = 0;
    this.eyeHeight = this.mv.eyeTarget; this.eyeVel = 0;
    this.landDip.set(0); this.landNod.set(0);
    this.fovSprint.set(0); this.fovSpeed.set(0);
    this.fov = this.cfg.fovBase;
    this.adsBlend = 0; this.adsHold = 0; this.breathT = 0;
  }

  /* --------------------------------------------------------------- recoil */

  /** Weapons push the camera through this. `pitchRad` up is positive. */
  addRecoil(pitchRad = 0, yawRad = 0) {
    const cfg = this.cfg;
    const absorb = cfg.recoilAbsorb;
    /* The absorbed part becomes real aim — exactly like COD, your shots walk
       the reticle up and it never fully comes back. */
    this.pitch = clamp(this.pitch + pitchRad * absorb, -cfg.pitchClamp, cfg.pitchClamp);
    this.yaw -= yawRad * absorb;
    /* The rest is a transient kick that springs home. */
    this.recoilPitch.x += pitchRad * (1 - absorb);
    this.recoilYaw.x += yawRad * (1 - absorb);
    this.recoilPitch.x = clamp(this.recoilPitch.x, -cfg.recoilMaxPitch, cfg.recoilMaxPitch);
    this.recoilYaw.x = clamp(this.recoilYaw.x, -cfg.recoilMaxPitch, cfg.recoilMaxPitch);
    this.recoilHold = cfg.recoilRecoverDelay;
  }

  /* ----------------------------------------------------------------- look */

  /** Consume mouse deltas. Called once per rendered frame (never in fixed). */
  applyLook(dx, dy, dt) {
    const cfg = this.cfg;
    const input = this.ctx?.input;
    const sens = (input?.sensitivity ?? cfg.lookSensitivity);
    const adsScale = 1 + (cfg.adsSensScale - 1) * this.adsBlend;
    const invert = input?.invertY ? -1 : 1;
    const ry = dx * sens * adsScale;
    const rp = dy * sens * adsScale * invert;
    this.yaw -= ry;
    this.pitch = clamp(this.pitch - rp, -cfg.pitchClamp, cfg.pitchClamp);

    /* the camera lags behind the input: kick the sway springs */
    const g = cfg.swayGain * (1 + (cfg.swayAdsScale - 1) * this.adsBlend);
    this.swayYaw.x = clamp(this.swayYaw.x + ry * g, -cfg.swayMaxYaw, cfg.swayMaxYaw);
    this.swayPitch.x = clamp(this.swayPitch.x + rp * g, -cfg.swayMaxPitch, cfg.swayMaxPitch);

    /* keep yaw bounded so long sessions never lose float precision */
    if (this.yaw > Math.PI * 2 || this.yaw < -Math.PI * 2) this.yaw %= Math.PI * 2;
  }

  /* --------------------------------------------------------------- update */

  update(dt, alpha) {
    const cfg = this.cfg;
    const mv = this.mv;
    const ctx = this.ctx;
    dt = Math.min(dt, 0.1);

    const ads = clamp(ctx?.weapons?.ads ?? 0, 0, 1);
    /* exponential, not a raw lerp — the blend must not depend on framerate */
    this.adsBlend += (ads - this.adsBlend) * (1 - Math.exp(-cfg.fovAdsEase * dt));
    if (ads > 0.6) this.adsHold += dt; else this.adsHold = 0;

    /* ---------------------------------------------------------- position */
    _v.copy(mv.prevFeet).lerp(mv.feet, clamp(alpha, 0, 1));

    /* ------------------------------------------------------- eye height */
    const eyeTarget = mv.eyeTarget;
    const omega = mv.sliding ? cfg.eyeSpringSlide : cfg.eyeSpring;
    /* exponential toward the target — never a teleport, never overshoots */
    this.eyeHeight += (eyeTarget - this.eyeHeight) * (1 - Math.exp(-omega * dt));

    /* --------------------------------------------------------- landing */
    if (mv.pendingLandImpact > 0) {
      const k = clamp(mv.pendingLandImpact / cfg.landRefSpeed, 0, 1.35);
      const e = k * k * 0.55 + k * 0.45;
      this.landDip.x -= cfg.landDipMax * e;
      this.landNod.x -= cfg.landNodMax * e;
      mv.pendingLandImpact = 0;
    }
    this.landDip.step(0, cfg.landSpring, dt, cfg.landSpringDamping);
    this.landNod.step(0, cfg.landSpring, dt, cfg.landSpringDamping);

    /* ------------------------------------------------------------- bob */
    const speedRef = clamp(mv.speed / cfg.bobSpeedRef, 0, cfg.bobMaxScale);
    let bobTarget = mv.grounded && !mv.sliding && !mv.mantling ? speedRef : cfg.bobAirScale;
    bobTarget *= 1 + (cfg.bobAdsScale - 1) * this.adsBlend;
    this.bobScale.step(bobTarget, cfg.bobBlend, dt);
    const bs = Math.max(0, this.bobScale.x);

    /* interpolate the sim-side phase so the bob is smooth at any framerate */
    const phase = mv.prevBobPhase + (mv.bobPhase - mv.prevBobPhase) * clamp(alpha, 0, 1);
    const bobX = Math.sin(phase) * cfg.bobAmpX * bs;
    const bobY = Math.sin(phase * 2) * cfg.bobAmpY * bs;
    this.bobRoll = Math.sin(phase) * cfg.bobAmpRoll * bs;
    this.bobOffset.set(bobX, bobY, Math.cos(phase * 2) * cfg.bobAmpY * 0.4 * bs);

    /* ------------------------------------------------------------ lean */
    const leanIn = clamp(this.leanInput || 0, -1, 1);
    let leanTarget = leanIn * cfg.leanMax;
    if (leanTarget !== 0 && !mv.mantling) leanTarget = this._clampLeanToWorld(leanTarget, _v);
    if (mv.sliding || mv.mantling) leanTarget = 0;
    this.lean.step(leanTarget, cfg.leanSpring, dt);
    this.leanOffset = this.lean.x;
    const leanRoll = -(this.leanOffset / cfg.leanMax) * cfg.leanRoll;

    /* ------------------------------------------------------------ roll */
    this.basis(this.yaw);
    const vLocalX = mv.velocity.x * _right.x + mv.velocity.z * _right.z;
    const vLocalZ = mv.velocity.x * _fwd.x + mv.velocity.z * _fwd.z;
    this.velocityLocal.set(vLocalX, mv.velocity.y, vLocalZ);

    const strafeN = clamp(vLocalX / Math.max(1e-3, cfg.speedWalk), -1, 1);
    let rollTarget = -strafeN * cfg.rollStrafe * (1 + (cfg.rollAdsScale - 1) * this.adsBlend);
    if (mv.sliding) {
      const sign = strafeN >= 0 ? 1 : -1;
      const dirSign = Math.abs(strafeN) < 0.05 ? 1 : sign;
      rollTarget += -dirSign * cfg.slideRoll * smoothstep(0, 0.18, mv.slideT) *
        (1 - smoothstep(cfg.slideDuration * 0.75, cfg.slideDuration, mv.slideT));
    }
    if (mv.mantling) {
      rollTarget += cfg.mantleRoll * Math.sin(Math.PI * mv.mantle.progress);
    }
    this.roll.step(rollTarget, cfg.rollSpring, dt);

    /* ------------------------------------------------------------ sway */
    this.swayYaw.step(0, cfg.swaySpring, dt);
    this.swayPitch.step(0, cfg.swaySpring, dt);
    this.viewSway.set(this.swayYaw.x, this.swayPitch.x);

    /* ---------------------------------------------------------- recoil */
    this.recoilHold = Math.max(0, this.recoilHold - dt);
    if (this.recoilHold <= 0) {
      this.recoilPitch.step(0, cfg.recoilSpring, dt, cfg.recoilSpringDamping);
      this.recoilYaw.step(0, cfg.recoilSpring * 0.85, dt, cfg.recoilSpringDamping);
    }
    this.recoilOffset.set(this.recoilPitch.x, this.recoilYaw.x);

    /* -------------------------------------------------------- breathing */
    this.breathT += dt;
    const still = 1 - clamp(mv.speed / (cfg.speedWalk * 0.6), 0, 1);
    let breathAmp = still * (1 + (cfg.breathAdsScale - 1) * this.adsBlend);
    if (this.adsHold > cfg.breathHoldTime) breathAmp *= cfg.breathHoldScale;
    if (!mv.grounded) breathAmp *= 0.25;
    const bp = Math.sin(this.breathT * Math.PI * 2 * cfg.breathRate) * 0.72 +
               Math.sin(this.breathT * Math.PI * 2 * cfg.breathRate2 + 1.7) * 0.28;
    const by = Math.sin(this.breathT * Math.PI * 2 * cfg.breathRate * 0.61 + 0.9);
    const breathPitch = bp * cfg.breathAmpPitch * breathAmp;
    const breathYaw = by * cfg.breathAmpYaw * breathAmp;

    /* -------------------------------------------------------------- fov */
    const sprintKick = mv.sprinting && mv.grounded
      ? cfg.fovSprintKick + (cfg.fovTacKick - cfg.fovSprintKick) * mv.tacBlend
      : 0;
    this.fovSprint.step(sprintKick, cfg.fovSprintEase, dt);
    this.fovSpeed.step(clamp(mv.speed / cfg.speedSprint, 0, 1) * cfg.fovSpeedKick, 3.5, dt);
    const hipFov = cfg.fovBase + this.fovSprint.x + this.fovSpeed.x;
    this.fov = hipFov + (cfg.fovAds - hipFov) * smoothstep(0, 1, this.adsBlend);

    /* ------------------------------------------------------ compose eye */
    const mantleDip = mv.mantling ? -0.06 * Math.sin(Math.PI * mv.mantle.progress) : 0;
    this.eye.set(
      _v.x + _right.x * (this.bobOffset.x + this.leanOffset),
      _v.y + this.eyeHeight + this.bobOffset.y + this.landDip.x + mantleDip,
      _v.z + _right.z * (this.bobOffset.x + this.leanOffset),
    );

    const mantleNod = mv.mantling
      ? -cfg.mantleDip * Math.sin(Math.PI * clamp(mv.mantle.progress * 1.15, 0, 1))
      : 0;

    _e.set(
      clamp(this.pitch + this.swayPitch.x + this.recoilPitch.x + breathPitch + this.landNod.x + mantleNod,
        -cfg.pitchClamp - 0.2, cfg.pitchClamp + 0.2),
      this.yaw + this.swayYaw.x + this.recoilYaw.x + breathYaw,
      this.roll.x + leanRoll + this.bobRoll,
      'YXZ',
    );
    this.quaternion.setFromEuler(_e);
  }

  basis(yaw) {
    _fwd.set(-Math.sin(yaw), 0, -Math.cos(yaw));
    _right.set(Math.cos(yaw), 0, -Math.sin(yaw));
  }

  /** Do not lean through a wall: shorten the offset to the nearest surface. */
  _clampLeanToWorld(target, feet) {
    const cfg = this.cfg;
    const sign = Math.sign(target);
    const dist = Math.abs(target);
    this.basis(this.yaw);
    _leanOrigin.set(feet.x, feet.y + this.eyeHeight, feet.z);
    _leanDir.set(_right.x * sign, 0, _right.z * sign);
    const hit = this.mv.adapter.raycast(_leanOrigin, _leanDir, dist + cfg.leanClearance + cfg.radius);
    if (!hit) return target;
    const allowed = Math.max(0, hit.distance - cfg.leanClearance - cfg.radius * 0.5);
    return sign * Math.min(dist, allowed);
  }

  /** Write the authoritative transform onto the engine cameras. */
  applyTo(ctx) {
    const cam = ctx.camera;
    cam.position.copy(this.eye);
    cam.quaternion.copy(this.quaternion);
    if (Math.abs(cam.fov - this.fov) > 1e-4) {
      cam.fov = this.fov;
      cam.updateProjectionMatrix();
    }
    cam.updateMatrixWorld(true);

    const vc = ctx.viewCamera;
    if (vc) {
      vc.position.copy(this.eye);
      vc.quaternion.copy(this.quaternion);
      vc.updateMatrixWorld(true);
    }
  }
}

export default CameraRig;
